'use client'

import { useEffect, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import { BookOpen, FileText, Images, Ruler, Video } from 'lucide-react'
import { Lightbox, type LightboxImage } from '@/components/content/lightbox'
import { optimizeSanityImageUrl } from '@/lib/sanity/client'
import type { BenchStepGroup, GalleryImage } from './bench-types'

type MediaTab = 'photos' | 'video' | 'blueprint' | 'gallery'

export const stepKey = (g: number, s: number) => `${g}:${s}`

export function BenchMediaPane({
  variant,
  collapsed = false,
  stepGroups,
  vimeoId,
  vimeoMinutes,
  blueprintUrl,
  artistsCorner = [],
  activeGroup,
  setActiveGroup,
  activeStep,
  setActiveStep,
  doneSteps,
  toggleStep,
  onOpenNotation,
}: {
  variant: 'component' | 'design'
  /** When the reference pane is collapsed, cap the media height at the fold so
   *  the (now-wider) photo/video can grow taller without scrolling. */
  collapsed?: boolean
  stepGroups: BenchStepGroup[]
  vimeoId?: string
  vimeoMinutes?: number | null
  blueprintUrl?: string
  artistsCorner?: GalleryImage[]
  activeGroup: number
  setActiveGroup: (n: number) => void
  activeStep: number
  setActiveStep: (n: number) => void
  doneSteps: Set<string>
  toggleStep: (key: string) => void
  /** Opens the notation-guide modal (trigger lives in the tab row). */
  onOpenNotation?: () => void
}) {
  const anyStep = stepGroups.some((g) => g.steps.length > 0)
  // Open on Video when one exists (it leads the tab row), then fall back
  // through steps → blueprint → gallery in the same left-to-right order.
  const [tab, setTab] = useState<MediaTab>(
    vimeoId
      ? 'video'
      : anyStep
        ? 'photos'
        : blueprintUrl
          ? 'blueprint'
          : artistsCorner.length > 0
            ? 'gallery'
            : 'photos',
  )
  const t = useTranslations('floggerBench')
  const [lb, setLb] = useState<{ open: boolean; images: LightboxImage[]; index: number }>({
    open: false,
    images: [],
    index: 0,
  })

  // When the reference pane is collapsed the media pane is wider, so the photo
  // (4:3) / video (16:9) grow taller — cap the height at the fold so the media
  // never runs off the bottom of the viewport.
  const foldCap = collapsed ? 'lg:max-h-[calc(100vh-16rem)]' : ''

  const group = stepGroups[activeGroup]
  const steps = useMemo(() => group?.steps ?? [], [group])
  const step = steps[activeStep]
  const multiGroup = stepGroups.length > 1
  const doneInGroup = steps.filter((_s, i) => doneSteps.has(stepKey(activeGroup, i))).length

  const stepImages = useMemo<LightboxImage[]>(
    () =>
      steps
        .filter((s) => s.imageUrl)
        .map((s) => ({ src: s.imageUrl!, caption: s.caption })),
    [steps],
  )

  const go = (dir: -1 | 1) => {
    const next = activeStep + dir
    if (next >= 0 && next < steps.length) setActiveStep(next)
  }

  // Keyboard ←/→ steps through the photos, but only while the Step photos tab
  // is showing and the lightbox (which runs its own arrow handler while open)
  // is closed. Ignores form fields and modifier combos, so a text cursor,
  // Cmd/Alt+← history nav, etc. are left untouched.
  useEffect(() => {
    if (tab !== 'photos' || steps.length < 2 || lb.open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return
      const t = e.target as HTMLElement | null
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          t.isContentEditable)
      ) {
        return
      }
      e.preventDefault()
      go(e.key === 'ArrowLeft' ? -1 : 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // `go` closes over activeStep + steps.length, both of which are deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, lb.open, activeStep, steps.length])

  const badge =
    variant === 'design' && group
      ? `${group.title} · ${t('stepOf', { step: activeStep + 1, total: steps.length })}`
      : multiGroup
        ? `${t('stepOf', { step: activeStep + 1, total: steps.length })} · ${t('chapterShort', { chapter: activeGroup + 1 })}`
        : t('stepOf', { step: activeStep + 1, total: steps.length })

  // A content-type icon per tab so the row reads at a glance: video, text
  // (step-by-step), blueprint, and gallery (Artist's Corner).
  const tabIcon = (id: MediaTab) => {
    const cls = 'h-3.5 w-3.5 shrink-0'
    if (id === 'video') return <Video className={cls} aria-hidden="true" />
    if (id === 'photos') return <FileText className={cls} aria-hidden="true" />
    if (id === 'blueprint') return <Ruler className={cls} aria-hidden="true" />
    return <Images className={cls} aria-hidden="true" />
  }
  const tabBtn = (id: MediaTab, label: string, disabled = false) => (
    <button
      key={id}
      type="button"
      disabled={disabled}
      onClick={() => setTab(id)}
      className="flex items-center gap-1.5 border-b-2 px-0.5 pb-2 text-[13px] transition-colors disabled:opacity-40"
      style={{
        color: tab === id ? 'var(--brand-700)' : '#5F647A',
        fontWeight: tab === id ? 700 : 500,
        borderColor: tab === id ? 'var(--brand-500)' : 'transparent',
      }}
    >
      {tabIcon(id)}
      {label}
    </button>
  )

  return (
    <div>
      <div className="border bg-white" style={{ borderColor: '#E2E4EC' }}>
        {/* Tab row */}
        <div className="flex items-center gap-5 border-b px-4 pt-3" style={{ borderColor: '#EEEFF4' }}>
          {vimeoId && tabBtn('video', vimeoMinutes ? t('videoMinutes', { minutes: vimeoMinutes }) : t('video'))}
          {anyStep && tabBtn('photos', t('stepByStep'))}
          {blueprintUrl && tabBtn('blueprint', t('blueprint'))}
          {artistsCorner.length > 0 && tabBtn('gallery', t('artistsCorner'))}
          {onOpenNotation && (
            <button
              type="button"
              onClick={onOpenNotation}
              className="ml-auto inline-flex shrink-0 items-center gap-1.5 pb-2 text-[13px] font-medium text-[#5F647A] transition-colors hover:text-[color:var(--brand-700)]"
            >
              <BookOpen className="h-3.5 w-3.5" aria-hidden />
              {t('notation')}
            </button>
          )}
        </div>

        {/* Media area */}
        {tab === 'photos' && (
          <>
            <div
              className="relative"
              role="group"
              aria-roledescription="carousel"
              aria-label={steps.length ? badge : t('stepPhotos')}
              aria-keyshortcuts="ArrowLeft ArrowRight"
            >
              {step?.imageUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={optimizeSanityImageUrl(step.imageUrl, 1200)}
                  alt={step.caption || badge}
                  onClick={() =>
                    setLb({ open: true, images: stepImages, index: Math.min(activeStep, stepImages.length - 1) })
                  }
                  className={`aspect-[4/3] w-full cursor-zoom-in object-cover ${foldCap}`}
                />
              ) : (
                <div
                  className={`flex aspect-[4/3] w-full items-center justify-center text-[13px] ${foldCap}`}
                  style={{ background: 'var(--brand-500)', color: 'rgba(255,255,255,0.7)' }}
                >
                  {steps.length ? t('stepPhoto') : t('noStepPhotos')}
                </div>
              )}
              {/* Step counter badge */}
              {steps.length > 0 && (
                <span
                  className="absolute left-3 top-3 px-2.5 py-1 text-[13px] font-bold text-white"
                  style={{ background: 'var(--brand-700)' }}
                >
                  {badge}
                </span>
              )}
              {/* Prev / next hit strips */}
              {steps.length > 1 && (
                <>
                  <button
                    type="button"
                    aria-label={t('previousStep')}
                    onClick={() => go(-1)}
                    disabled={activeStep === 0}
                    className="group absolute inset-y-0 left-0 flex w-12 items-center justify-center disabled:opacity-30"
                  >
                    <span className="flex h-8 w-8 items-center justify-center text-white transition-opacity group-hover:opacity-60" style={{ background: 'rgba(0,0,0,0.4)' }}>
                      ‹
                    </span>
                  </button>
                  <button
                    type="button"
                    aria-label={t('nextStep')}
                    onClick={() => go(1)}
                    disabled={activeStep === steps.length - 1}
                    className="group absolute inset-y-0 right-0 flex w-12 items-center justify-center disabled:opacity-30"
                  >
                    <span className="flex h-8 w-8 items-center justify-center text-white transition-opacity group-hover:opacity-60" style={{ background: 'rgba(0,0,0,0.4)' }}>
                      ›
                    </span>
                  </button>
                </>
              )}
            </div>
            {/* Caption + done toggle */}
            {step && (
              <div className="border-t px-4 py-3.5" style={{ borderColor: '#EEEFF4' }}>
                {/* Fixed 3-row height so stepping through steps never shifts
                    the panel up/down; longer captions clamp with an ellipsis. */}
                <p
                  className="line-clamp-3 min-h-[4.9em] text-[14.5px] leading-relaxed"
                  style={{ color: '#374151' }}
                  title={step.caption}
                  aria-live="polite"
                >
                  {step.caption}
                </p>
                <label className="mt-2 flex cursor-pointer items-center gap-2 text-[13px]" style={{ color: 'var(--brand-700)' }}>
                  <input
                    type="checkbox"
                    checked={doneSteps.has(stepKey(activeGroup, activeStep))}
                    onChange={() => toggleStep(stepKey(activeGroup, activeStep))}
                    className="h-4 w-4 accent-[color:var(--brand-500)]"
                  />
                  {t('markStepDone')}
                </label>
              </div>
            )}
          </>
        )}

        {tab === 'video' && vimeoId && (
          <div className={`aspect-video w-full bg-black ${foldCap}`}>
            <iframe
              src={`https://player.vimeo.com/video/${vimeoId}?playsinline=1&airplay=1`}
              className="h-full w-full"
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media; airplay"
              allowFullScreen
              title={t('video')}
            />
          </div>
        )}

        {tab === 'blueprint' && blueprintUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={optimizeSanityImageUrl(blueprintUrl, 1600)}
            alt={t('blueprint')}
            onClick={() => setLb({ open: true, images: [{ src: blueprintUrl, caption: t('blueprint') }], index: 0 })}
            className={`w-full cursor-zoom-in object-contain ${foldCap}`}
          />
        )}

        {tab === 'gallery' && artistsCorner.length > 0 && (
          <div className="grid grid-cols-2 gap-1 p-1">
            {artistsCorner.map((img, i) => (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                key={i}
                src={optimizeSanityImageUrl(img.url, 800)}
                alt={img.alt || img.caption || t('artistsCorner')}
                onClick={() =>
                  setLb({
                    open: true,
                    images: artistsCorner.map((g) => ({ src: g.url, caption: g.caption, alt: g.alt })),
                    index: i,
                  })
                }
                className="aspect-square w-full cursor-zoom-in object-cover"
              />
            ))}
          </div>
        )}
      </div>

      {/* Progress under the card (photos tab) */}
      {tab === 'photos' && steps.length > 0 && variant === 'component' && (
        <div className="mt-3">
          <div className="h-1.5 w-full" style={{ background: '#E2E4EC' }}>
            <div
              className="h-full transition-[width]"
              style={{ background: 'var(--brand-500)', width: `${(doneInGroup / steps.length) * 100}%` }}
            />
          </div>
          <div className="mt-1 text-[13px] font-bold" style={{ color: 'var(--brand-700)' }}>
            {t('doneOf', { done: doneInGroup, total: steps.length })}
          </div>
        </div>
      )}

      {/* Chapter toggle (component with 2+ groups) */}
      {variant === 'component' && multiGroup && (
        <div className="mt-3 flex gap-2">
          {stepGroups.map((g, i) => {
            const active = i === activeGroup
            return (
              <button
                key={g.key}
                type="button"
                onClick={() => {
                  setActiveGroup(i)
                  setActiveStep(0)
                }}
                className="flex-1 border px-3 py-2 text-[12.5px] font-bold"
                style={{
                  borderColor: active ? 'var(--brand-200)' : '#E2E4EC',
                  background: active ? 'var(--brand-50)' : '#fff',
                  color: active ? 'var(--brand-700)' : '#5F647A',
                }}
              >
                {g.title}
              </button>
            )
          })}
        </div>
      )}

      {/* Chapter intro (component): the active group's description, under its
          title — the active toggle button above when there are 2+ chapters,
          or a title line here for a single-chapter page. Plain localeText;
          blank-line paragraph breaks are kept via pre-line. */}
      {variant === 'component' && group?.description && (
        <div className="mt-3 border-l-2 pl-3" style={{ borderColor: 'var(--brand-200)' }}>
          {!multiGroup && group.title && (
            <p className="mb-1 text-[13px] font-bold" style={{ color: 'var(--brand-700)' }}>
              {group.title}
            </p>
          )}
          <p
            lang={group.descriptionLang || undefined}
            className="whitespace-pre-line text-[14px] leading-relaxed"
            style={{ color: '#374151' }}
          >
            {group.description}
          </p>
        </div>
      )}

      {/* Per-section progress (design) */}
      {variant === 'design' && stepGroups.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {stepGroups.map((g, i) => {
            const gDone = g.steps.filter((_s, si) => doneSteps.has(stepKey(i, si))).length
            const total = g.steps.length || 1
            const complete = gDone === g.steps.length && g.steps.length > 0
            const active = i === activeGroup
            return (
              <button
                key={g.key}
                type="button"
                onClick={() => {
                  setActiveGroup(i)
                  setActiveStep(0)
                }}
                className="flex w-full items-center gap-2 text-left"
              >
                <span className="w-[84px] shrink-0 truncate text-[12px] font-bold" style={{ color: 'var(--brand-700)' }}>
                  {g.title}
                </span>
                <span className="h-1.5 flex-1" style={{ background: '#E2E4EC' }}>
                  <span
                    className="block h-full"
                    style={{
                      width: `${(gDone / total) * 100}%`,
                      background: complete ? 'var(--success)' : active ? 'var(--brand-500)' : 'var(--brand-200)',
                    }}
                  />
                </span>
                <span className="min-w-[54px] shrink-0 whitespace-nowrap text-right text-[11.5px]" style={{ color: '#5F647A' }}>
                  {t('doneFraction', { done: gDone, total: g.steps.length })}
                </span>
              </button>
            )
          })}
        </div>
      )}

      <Lightbox
        images={lb.images}
        open={lb.open}
        initialIndex={lb.index}
        loop={false}
        onClose={() => setLb((s) => ({ ...s, open: false }))}
      />
    </div>
  )
}
